/**
 * Flow-Registry: Flows pro Modul (Schleifchen, Clubmeisterschaft).
 * Config-first: loads flows.config.json, merges with code fallback (steps with functions).
 * FLOWS built from list only (no object literal). Dev asserts: no duplicate ids/areas, required fields.
 */

import flowsConfig from "./flows.config.json";

const isDev =
  (typeof process !== "undefined" && process.env?.NODE_ENV !== "production") ||
  (typeof import.meta !== "undefined" && import.meta.env?.DEV);

/** Code fallback: full flow descriptors including steps (with apply/maybeFinalize). Config cannot hold functions. */
const CODE_FALLBACK_FLOWS = [
  {
    id: "schleifchen.turnier_setup",
    area: "schleifchen",
    startStepId: "basics",
    steps: {
      basics: {
        messageKey: "ai.flow.schleifchen.step.basics.title",
        quickReplies: [{ key: "ai.flow.action.continue", value: "__continue" }],
        apply() {
          return "mode";
        },
      },
      mode: {
        messageKey: "ai.flow.schleifchen.step.mode.title",
        quickReplies: [
          { key: "settings.wizard.modePreset.classic", value: "classic" },
          { key: "settings.wizard.modePreset.competitive", value: "competitive" },
          { key: "settings.wizard.modePreset.playoffs", value: "playoffs" },
        ],
        apply() {
          return "players";
        },
      },
      players: {
        messageKey: "ai.flow.schleifchen.step.players.title",
        quickReplies: [
          { key: "ai.flow.schleifchen.players.8", value: "8" },
          { key: "ai.flow.schleifchen.players.12", value: "12" },
          { key: "ai.flow.schleifchen.players.16", value: "16" },
        ],
        apply() {
          return "review";
        },
      },
      review: {
        messageKey: "ai.flow.schleifchen.review.title",
        quickReplies: [
          { key: "ai.flow.action.openWizard", value: "__open_wizard" },
          { key: "ai.flow.action.saveDraft", value: "__save_draft" },
          { key: "ai.flow.common.cancel", value: "__cancel" },
        ],
        apply(answer) {
          if (answer === "__cancel") return null;
          return null;
        },
        maybeFinalize() {
          return { toolCall: { name: "navigation.goTo", args: { route: "/schleifchen/setup?new=1" } } };
        },
      },
    },
  },
  {
    id: "clubmeisterschaft.setup",
    area: "clubmeisterschaft",
    startStepId: "basics",
    steps: {
      basics: {
        messageKey: "ai.flow.club.step.basics.title",
        quickReplies: [{ key: "ai.flow.action.continue", value: "__continue" }],
        apply() {
          return "mode";
        },
      },
      mode: {
        messageKey: "ai.flow.club.step.mode.title",
        quickReplies: [
          { key: "ai.flow.club.mode.groups", value: "groups" },
          { key: "ai.flow.club.mode.ko", value: "ko" },
        ],
        apply() {
          return "groups";
        },
      },
      groups: {
        messageKey: "ai.flow.club.step.groups.title",
        quickReplies: [{ key: "ai.flow.action.continue", value: "__continue" }],
        apply() {
          return "review";
        },
      },
      review: {
        messageKey: "ai.flow.club.review.title",
        quickReplies: [
          { key: "ai.flow.action.openWizard", value: "__open_wizard" },
          { key: "ai.flow.action.saveDraft", value: "__save_draft" },
          { key: "ai.flow.common.cancel", value: "__cancel" },
        ],
        apply(answer) {
          if (answer === "__cancel") return null;
          return null;
        },
        maybeFinalize() {
          return { toolCall: { name: "navigation.goTo", args: { route: "/clubmeisterschaft/setup?new=1" } } };
        },
      },
    },
  },
];

function assertRequiredFields(list) {
  if (!isDev) return;
  for (let i = 0; i < list.length; i++) {
    const f = list[i];
    if (!f || typeof f !== "object") {
      throw new Error(`[flow.registry] flow at index ${i} must be an object`);
    }
    if (!f.id || typeof f.id !== "string" || !f.id.trim()) {
      throw new Error(`[flow.registry] flow at index ${i} missing required field: id`);
    }
    if (!f.area || typeof f.area !== "string" || !f.area.trim()) {
      throw new Error(`[flow.registry] flow at index ${i} missing required field: area`);
    }
    if (!f.startStepId || typeof f.startStepId !== "string" || !f.startStepId.trim()) {
      throw new Error(`[flow.registry] flow at index ${i} missing required field: startStepId`);
    }
  }
}

/** Build flow list: config list merged with code fallback (full steps). */
function buildFlowList() {
  const configFlows = flowsConfig?.flows;
  if (configFlows && Array.isArray(configFlows) && configFlows.length > 0) {
    const list = configFlows.map((c) => {
      const full = CODE_FALLBACK_FLOWS.find((f) => f.id === c.id || f.area === (c.area || "").trim());
      if (full) return full;
      return {
        id: (c.id || "").trim() || "unknown",
        area: (c.area || "").trim() || "unknown",
        startStepId: (c.startStepId || "basics").trim(),
        steps: {},
      };
    });
    assertRequiredFields(list);
    return list;
  }
  assertRequiredFields(CODE_FALLBACK_FLOWS);
  return [...CODE_FALLBACK_FLOWS];
}

const flowList = buildFlowList();

function buildFlowsMap(list) {
  const seenArea = new Set();
  const seenId = new Set();
  const entries = list.map((f) => {
    if (isDev) {
      if (seenArea.has(f.area)) {
        throw new Error(`[flow.registry] duplicate flow area: ${f.area}`);
      }
      if (seenId.has(f.id)) {
        throw new Error(`[flow.registry] duplicate flow id: ${f.id}`);
      }
      seenArea.add(f.area);
      seenId.add(f.id);
    }
    return [f.area, f];
  });
  return Object.fromEntries(entries);
}

export const FLOWS = buildFlowsMap(flowList);

export function getFlow(flowId) {
  return Object.values(FLOWS).find((f) => f.id === flowId) ?? null;
}
